import {Card, FormControl, FormField, Label} from 'platform/components';
import {Grid, GridItem, Link, Text, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {UseFormReturn} from 'react-hook-form';

import {always} from 'ramda';

import {CorrectiveTaxDocumentV5ResponseBodyRead} from '@dms/api/core';
import i18n from '@dms/i18n';
import {accountingRoutes} from '@dms/routes';
import {EMPTY_PLACEHOLDER} from '@dms/shared';

import {composePath, suffixTestId, TestIdProps, useNavigate} from 'shared/web';

import {AccountingDocumentFormValues} from '../../types';

interface CorrectiveTaxDocumentDetailsProps extends TestIdProps {
  control: FormControl<AccountingDocumentFormValues>;
  formApi: UseFormReturn<AccountingDocumentFormValues>;
  correctiveTaxDocument: CorrectiveTaxDocumentV5ResponseBodyRead | undefined;
}

export function CorrectiveTaxDocumentDetails(props: CorrectiveTaxDocumentDetailsProps) {
  const navigate = useNavigate();

  const {description} = props.formApi.getValues();

  const handleNavigateToInvoice = () => {
    const path = match(props.correctiveTaxDocument?.invoicingDocumentType)
      .with('invoice', always(accountingRoutes.invoiceDetail))
      .with('balance_invoice', always(accountingRoutes.balanceInvoiceDetail))
      .with('invoice_proforma', always(accountingRoutes.proformaInvoiceDetail))
      .with('tax_document_for_payment', always(accountingRoutes.taxDocumentForPaymentDetail))
      .otherwise(always(accountingRoutes.invoiceDetail));

    navigate(composePath(path, {params: {id: props.correctiveTaxDocument?.invoicingDocumentId}}));
  };

  return (
    <Card title={i18n.t('entity.correctiveTaxDocument.labels.taxDocumentDetails')}>
      <Grid columns={4}>
        <VStack>
          <Label>{i18n.t('entity.warehouse.labels.invoiceNumber')}</Label>
          <Link
            title={props.correctiveTaxDocument?.invoicingDocumentNumber}
            onClick={handleNavigateToInvoice}
            data-testid={suffixTestId('invoice-number', props)}
          />
        </VStack>

        <GridItem span={3}>
          <VStack>
            <Label>{i18n.t('general.labels.description')}</Label>
            <Text size="small">{description || EMPTY_PLACEHOLDER}</Text>
          </VStack>
        </GridItem>

        <FormField
          name="dateOfReception"
          control={props.control}
          type="apiDate"
          isRequired
          isDisabled
          label={i18n.t('entity.correctiveTaxDocument.labels.dateOfReception')}
        />

        <GridItem span={3}>
          <FormField
            name="reason"
            control={props.control}
            type="textarea"
            isRequired
            isDisabled
            label={i18n.t('general.labels.reason')}
          />
        </GridItem>
      </Grid>
    </Card>
  );
}
